import React, { useMemo, useState } from 'react';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import type { Campaign } from './data';
import { type Filters, format } from './model';
import { type CatalogueFilters, dateRange, filterCatalogue } from './reportState';
import { metadataNumber, opportunityValues, topOpportunities } from './selectionModel';

type Props = {
  campaigns: Campaign[];
  filters: Filters;
  side: 'current' | 'comparison';
  loading: boolean;
  error?: string;
  onClose: () => void;
  onSelect: (campaign: Campaign) => void;
};

export default function LPCOfferPicker({ campaigns, filters, side, loading, error, onClose, onSelect }: Props) {
  const [facets, setFacets] = useState<CatalogueFilters>({ search: '', country: [], destination: [], currency: [] });
  const [top50, setTop50] = useState(false);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(filters[side]);
  const compact = useMediaQuery(useTheme().breakpoints.down('md'));
  const pageSize = useMediaQuery('(min-height: 950px)') ? 8 : 5;
  const ranked = campaigns.some((campaign) => campaign.source_top50_rank !== undefined);
  const options = useMemo(
    () => ({
      country: [...new Set(campaigns.map((campaign) => campaign.offer_country_name || 'Unknown'))].sort(),
      destination: [...new Set(campaigns.map((campaign) => campaign.offer_geo_area_level_1 || 'Unknown'))].sort(),
      currency: [...new Set(campaigns.flatMap((campaign) => campaign.vendor_currencies || []))].sort(),
    }),
    [campaigns],
  );
  const filtered = useMemo(
    () => filterCatalogue(topOpportunities(campaigns, top50), facets),
    [campaigns, top50, facets],
  );
  const visiblePage = Math.min(page, Math.max(0, Math.ceil(filtered.length / pageSize) - 1));
  const chosen = campaigns.find((campaign) => campaign.offer_bk === selected);
  const rows = filtered.slice(visiblePage * pageSize, (visiblePage + 1) * pageSize);
  const set = (changes: Partial<CatalogueFilters>) => {
    setFacets((previous) => ({ ...previous, ...changes }));
    setPage(0);
  };
  const values = (campaign: Campaign) => opportunityValues(campaign, filters.currency, filters.refund);
  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      fullWidth
      aria-labelledby="lpc-offer-picker-title"
      sx={{ '& .MuiDialog-paper': { width: 'calc(100% - 48px)', maxWidth: 1800, m: 1.5 } }}
    >
      <DialogTitle id="lpc-offer-picker-title">{side === 'current' ? 'Choose offer' : 'Choose comparison'}</DialogTitle>
      <DialogContent dividers>
        <Stack gap={1.5} sx={{ overflow: 'visible' }}>
          <TextField
            size="small"
            fullWidth
            label="Search offer, name or ID"
            value={facets.search}
            onChange={(event) => set({ search: event.target.value })}
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3,minmax(0,1fr))' }, gap: 1 }}>
            {(['country', 'destination', 'currency'] as const).map((key) => (
              <Autocomplete
                key={key}
                multiple
                size="small"
                options={options[key]}
                value={facets[key]}
                onChange={(_, value) => set({ [key]: value })}
                renderInput={(props) => (
                  <TextField
                    {...props}
                    label={
                      { country: 'Hotel country', destination: 'Destination / state', currency: 'Vendor currency' }[key]
                    }
                  />
                )}
              />
            ))}
          </Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
            <Typography variant="caption">{filtered.length.toLocaleString()} offers</Typography>
            <Stack direction="row" alignItems="center" gap={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={top50}
                    disabled={!ranked}
                    onChange={(_, checked) => {
                      setTop50(checked);
                      setPage(0);
                    }}
                  />
                }
                label="Source top 50"
              />
              <Button onClick={() => set({ search: '', country: [], destination: [], currency: [] })}>
                Clear filters
              </Button>
            </Stack>
          </Stack>
          {error && <Alert severity="error">{error}</Alert>}
          {loading && <Alert severity="info">Loading offer catalogue…</Alert>}
          {!loading && !error && !filtered.length && <Alert severity="info">No offers match these filters.</Alert>}
          {compact ? (
            <Stack gap={1}>
              {rows.map((campaign) => (
                <Button
                  key={campaign.offer_bk}
                  variant="outlined"
                  onClick={() => setSelected(campaign.offer_bk)}
                  aria-pressed={selected === campaign.offer_bk}
                  sx={{ justifyContent: 'start', textAlign: 'left', p: 1.5 }}
                >
                  <Box>
                    <Typography fontWeight={600}>{campaign.offer_opportunity_name}</Typography>
                    <Typography variant="caption">
                      {campaign.offer_country_name} · {campaign.offer_geo_area_level_1}
                    </Typography>
                    <Typography variant="caption" display="block">
                      Sales: {dateRange(campaign.start_date, campaign.end_date)}
                    </Typography>
                    <Typography variant="caption" display="block">
                      {format(values(campaign).nights)} room nights ·{' '}
                      {format(values(campaign).revenue, 'money', values(campaign).currency)}
                    </Typography>
                  </Box>
                </Button>
              ))}
            </Stack>
          ) : (
            <TableContainer>
              <Table
                size="small"
                aria-label="LPC offer catalogue"
                sx={{
                  width: '100%',
                  tableLayout: 'fixed',
                  '& .MuiTableCell-root': { overflowWrap: 'anywhere', py: 1, px: 1 },
                  '& .MuiTableHead-root .MuiTableCell-root': { bgcolor: 'primary.main', color: 'primary.contrastText' },
                }}
              >
                <TableHead>
                  <TableRow>
                    {[
                      'Select',
                      'Offer / opportunity',
                      'Hotel country',
                      'Destination / state',
                      'Observed sales dates',
                      'Room nights',
                      'Vendor revenue after holdback',
                      'Last 12 months TTV (AUD)',
                    ].map((label, index) => (
                      <TableCell key={label} sx={{ width: [5, 24, 10, 12, 17, 8, 12, 12][index] + '%' }}>
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((campaign) => (
                    <TableRow
                      key={campaign.offer_bk}
                      hover
                      selected={selected === campaign.offer_bk}
                      onClick={() => setSelected(campaign.offer_bk)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Radio
                          checked={selected === campaign.offer_bk}
                          onChange={() => setSelected(campaign.offer_bk)}
                          inputProps={{ 'aria-label': `Select ${campaign.offer_opportunity_name}` }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {campaign.offer_opportunity_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {campaign.offer_bk}
                        </Typography>
                      </TableCell>
                      <TableCell>{campaign.offer_country_name || 'Unknown'}</TableCell>
                      <TableCell>{campaign.offer_geo_area_level_1 || 'Unknown'}</TableCell>
                      <TableCell>{dateRange(campaign.start_date, campaign.end_date)}</TableCell>
                      <TableCell align="right">{format(values(campaign).nights)}</TableCell>
                      <TableCell align="right">
                        {format(values(campaign).revenue, 'money', values(campaign).currency)}
                      </TableCell>
                      <TableCell align="right">
                        {format(metadataNumber(campaign.l12m_ttv_aud), 'money', 'AUD')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <TablePagination
            component="div"
            count={filtered.length}
            page={visiblePage}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[]}
            onPageChange={(_, value) => setPage(value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!chosen || loading || !!error} onClick={() => chosen && onSelect(chosen)}>
          Use offer
        </Button>
      </DialogActions>
    </Dialog>
  );
}
